import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ShieldAlert,
  Clock,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Send,
  Loader2,
  Building,
} from 'lucide-react';
import { useSubscriptionPlans, type SubscriptionPlan } from '@/hooks/useSubscriptionPlans';
import {
  useSubscription,
  usePendingUpgradeRequest,
  useRequestUpgrade,
} from '@/hooks/useBillingAndUsage';
import { useUserCurrency } from '@/hooks/useUserCurrency';
import { useIsAdmin } from '@/hooks/useUserRole';
import { formatInr, formatUsd, SALES_EMAIL } from '@/lib/constants/plans';

export interface SubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetPlanName?: string;
  initialInterval?: 'monthly' | 'yearly';
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  open,
  onOpenChange,
  targetPlanName,
  initialInterval = 'yearly',
}) => {
  const { data: plans = [], isLoading: plansLoading } = useSubscriptionPlans();
  const { data: currentSubscription } = useSubscription();
  const { data: pendingRequest, isLoading: pendingLoading } = usePendingUpgradeRequest();
  const { currency } = useUserCurrency();
  const isAdmin = useIsAdmin();
  const { mutate: requestUpgrade, isPending: isSubmitting } = useRequestUpgrade();

  const [selectedPlanName, setSelectedPlanName] = useState<string>('Starter');
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('yearly');
  const [notes, setNotes] = useState<string>('');

  // Synchronize selection when targetPlanName or initialInterval changes
  useEffect(() => {
    if (targetPlanName) {
      setSelectedPlanName(targetPlanName);
    } else if (currentSubscription) {
      if (currentSubscription.plan_name === 'Free Trial') {
        setSelectedPlanName('Starter');
      } else if (currentSubscription.plan_name === 'Starter') {
        setSelectedPlanName('Professional');
      } else if (currentSubscription.plan_name === 'Professional') {
        setSelectedPlanName('Scale');
      } else {
        setSelectedPlanName('Enterprise');
      }
    }
    if (initialInterval) {
      setBillingInterval(initialInterval);
    }
  }, [targetPlanName, initialInterval, currentSubscription, open]);

  const selectedPlan: SubscriptionPlan | undefined = plans.find(
    (p) => p.plan_name.toLowerCase() === selectedPlanName.toLowerCase()
  );

  const isEnterprise = selectedPlan?.plan_name === 'Enterprise';
  const isCurrentActivePlan =
    currentSubscription?.plan_name.toLowerCase() === selectedPlan?.plan_name.toLowerCase() &&
    currentSubscription?.billing_interval === billingInterval &&
    currentSubscription?.status === 'active';

  const formatLimit = (limit: number | undefined) => {
    if (limit === undefined) return '—';
    if (limit < 0) return 'Unlimited';
    return limit.toLocaleString('en-US');
  };

  const getPriceDisplay = () => {
    if (!selectedPlan) return null;
    if (isEnterprise) {
      return {
        amount: 'Custom Pricing',
        period: 'Contact Sales',
        savings: null,
      };
    }

    const isAnnual = billingInterval === 'yearly';
    if (currency === 'INR') {
      if (isAnnual) {
        return {
          amount: selectedPlan.annual_monthly_rate_inr
            ? formatInr(selectedPlan.annual_monthly_rate_inr)
            : formatInr(Math.round((selectedPlan.annual_price_inr ?? 0) / 12)),
          period: '/month, billed annually',
          fullAnnual: selectedPlan.annual_price_inr ? formatInr(selectedPlan.annual_price_inr) : null,
          savings: 'Save 20% on Annual billing',
        };
      }
      return {
        amount: selectedPlan.monthly_price_inr ? formatInr(selectedPlan.monthly_price_inr) : '—',
        period: '/month',
        savings: null,
      };
    } else {
      if (isAnnual) {
        return {
          amount: selectedPlan.annual_monthly_rate_usd
            ? formatUsd(selectedPlan.annual_monthly_rate_usd)
            : formatUsd(Math.round((selectedPlan.annual_price_usd ?? 0) / 12)),
          period: '/month, billed annually',
          fullAnnual: selectedPlan.annual_price_usd ? formatUsd(selectedPlan.annual_price_usd) : null,
          savings: 'Save 20% on Annual billing',
        };
      }
      return {
        amount: selectedPlan.monthly_price_usd ? formatUsd(selectedPlan.monthly_price_usd) : '—',
        period: '/month',
        savings: null,
      };
    }
  };

  const priceInfo = getPriceDisplay();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlan || !isAdmin || pendingRequest) return;

    if (isEnterprise) {
      requestUpgrade(
        {
          planName: 'Enterprise',
          billingInterval: 'custom',
          notes: notes.trim() || 'Enterprise plan consultation request',
        },
        {
          onSuccess: () => {
            onOpenChange(false);
          },
        }
      );
      return;
    }

    requestUpgrade(
      {
        planName: selectedPlan.plan_name,
        billingInterval,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-100 text-purple-600">
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <DialogTitle className="text-xl font-bold text-gray-900">
                Upgrade Organization Plan
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Unlock higher limits, advanced metrics, and scale your team.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {plansLoading || pendingLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 pt-2">
            {/* Pending Request Alert */}
            {pendingRequest && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                <div className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-5 w-5 text-amber-600 shrink-0" />
                  <div>
                    <h4 className="font-semibold text-sm">
                      Upgrade Request Pending Review
                    </h4>
                    <p className="mt-1 text-xs text-amber-800">
                      Your organization already has a pending upgrade request for the{' '}
                      <strong>{pendingRequest.requested_plan}</strong> tier submitted on{' '}
                      {new Date(pendingRequest.created_at).toLocaleDateString()}. Our team is
                      processing it and will contact your administrator shortly.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Non-Admin Alert */}
            {!isAdmin && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-900">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="mt-0.5 h-5 w-5 text-blue-600 shrink-0" />
                  <div>
                    <h4 className="font-semibold text-sm">Administrator Access Required</h4>
                    <p className="mt-1 text-xs text-blue-800">
                      Only organization administrators can submit plan upgrade requests. Please
                      contact your organization owner or admin to complete this request.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Plan Tier Selector */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Select Target Tier
              </Label>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {plans.map((plan) => {
                  const isSelected =
                    plan.plan_name.toLowerCase() === selectedPlanName.toLowerCase();
                  const isCurrent =
                    currentSubscription?.plan_name.toLowerCase() ===
                    plan.plan_name.toLowerCase();

                  return (
                    <button
                      key={plan.plan_name}
                      type="button"
                      onClick={() => setSelectedPlanName(plan.plan_name)}
                      className={`relative flex flex-col items-center justify-center rounded-xl border p-3 text-center transition-all ${
                        isSelected
                          ? 'border-purple-600 bg-purple-50/50 shadow-sm ring-2 ring-purple-600'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {isCurrent && (
                        <Badge
                          variant="secondary"
                          className="absolute -top-2 rounded-full px-1.5 py-0 text-[10px]"
                        >
                          Current
                        </Badge>
                      )}
                      <span className="text-sm font-bold text-gray-900">
                        {plan.display_name}
                      </span>
                      <span className="mt-0.5 text-[11px] text-gray-500 line-clamp-1">
                        {plan.influencer_limit < 0
                          ? 'Unlimited'
                          : `${plan.influencer_limit.toLocaleString()} infl.`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Billing Interval Toggle (if not enterprise) */}
            {!isEnterprise ? (
              <div className="flex items-center justify-between rounded-xl bg-gray-50 p-2 border border-gray-200">
                <span className="text-xs font-medium text-gray-700 ml-2">
                  Billing Cycle
                </span>
                <div className="flex items-center gap-1 rounded-lg bg-gray-200/70 p-1">
                  <button
                    type="button"
                    onClick={() => setBillingInterval('monthly')}
                    className={`rounded-md px-3 py-1 text-xs font-medium transition-all ${
                      billingInterval === 'monthly'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingInterval('yearly')}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-all ${
                      billingInterval === 'yearly'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <span>Annual</span>
                    <span className="rounded-full bg-amber-400 px-1.5 py-0.2 text-[10px] font-bold text-gray-950">
                      Save 20%
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-xl bg-purple-50/70 border border-purple-200 p-3 text-xs text-purple-900 flex items-center gap-2">
                <Building className="h-4 w-4 text-purple-600 shrink-0" />
                <span>Enterprise contracts include custom terms, SLA guarantees, and tailored billing cycles.</span>
              </div>
            )}

            {/* Price Preview Card */}
            <div className="rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50/50 to-white p-5">
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {selectedPlan?.display_name} Plan
                  </h3>
                  <p className="text-xs text-gray-500">{selectedPlan?.tagline}</p>
                </div>
                <div className="text-left sm:text-right">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-extrabold text-purple-900">
                      {priceInfo?.amount}
                    </span>
                    <span className="text-xs text-gray-500">{priceInfo?.period}</span>
                  </div>
                  {priceInfo?.fullAnnual && (
                    <p className="text-[11px] text-gray-500">
                      {priceInfo.fullAnnual} billed once per year
                    </p>
                  )}
                  {priceInfo?.savings && (
                    <p className="text-[11px] font-semibold text-emerald-600">
                      {priceInfo.savings}
                    </p>
                  )}
                </div>
              </div>

              {/* Limits Comparison Matrix */}
              <div className="mt-4 pt-4 border-t border-purple-100/80">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Limits Comparison
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <div className="rounded-lg bg-white/80 p-2.5 border border-purple-100">
                    <span className="text-gray-500 block">Influencers</span>
                    <div className="flex items-center gap-1.5 mt-1 font-semibold text-gray-900">
                      <span>{formatLimit(currentSubscription?.influencer_limit)}</span>
                      <ArrowRight className="h-3 w-3 text-purple-600 shrink-0" />
                      <span className="text-purple-700">
                        {formatLimit(selectedPlan?.influencer_limit)}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg bg-white/80 p-2.5 border border-purple-100">
                    <span className="text-gray-500 block">Active Campaigns</span>
                    <div className="flex items-center gap-1.5 mt-1 font-semibold text-gray-900">
                      <span>{formatLimit(currentSubscription?.campaign_limit)}</span>
                      <ArrowRight className="h-3 w-3 text-purple-600 shrink-0" />
                      <span className="text-purple-700">
                        {formatLimit(selectedPlan?.campaign_limit)}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg bg-white/80 p-2.5 border border-purple-100">
                    <span className="text-gray-500 block">Team Seats</span>
                    <div className="flex items-center gap-1.5 mt-1 font-semibold text-gray-900">
                      <span>{formatLimit(currentSubscription?.team_member_limit)}</span>
                      <ArrowRight className="h-3 w-3 text-purple-600 shrink-0" />
                      <span className="text-purple-700">
                        {formatLimit(selectedPlan?.team_member_limit)}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg bg-white/80 p-2.5 border border-purple-100">
                    <span className="text-gray-500 block">Monthly Deliverables</span>
                    <div className="flex items-center gap-1.5 mt-1 font-semibold text-gray-900">
                      <span>{formatLimit(currentSubscription?.deliverable_limit)}</span>
                      <ArrowRight className="h-3 w-3 text-purple-600 shrink-0" />
                      <span className="text-purple-700">
                        {formatLimit(selectedPlan?.deliverable_limit)}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg bg-white/80 p-2.5 border border-purple-100">
                    <span className="text-gray-500 block">Monthly Profile Refreshes</span>
                    <div className="flex items-center gap-1.5 mt-1 font-semibold text-gray-900">
                      <span>{formatLimit(currentSubscription?.profile_refresh_limit)}</span>
                      <ArrowRight className="h-3 w-3 text-purple-600 shrink-0" />
                      <span className="text-purple-700">
                        {formatLimit(selectedPlan?.profile_refresh_limit)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature Highlights */}
              {selectedPlan?.features && selectedPlan.features.length > 0 && (
                <div className="mt-4 pt-3 border-t border-purple-100/80">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {selectedPlan.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs text-gray-700">
                        <CheckCircle2 className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Optional Notes */}
            <div>
              <Label htmlFor="upgrade-notes" className="text-xs font-medium text-gray-700">
                Notes for Sales / Account Team (Optional)
              </Label>
              <Textarea
                id="upgrade-notes"
                placeholder={
                  isEnterprise
                    ? "Tell us about your organization's requirements, custom seat count, or preferred start date..."
                    : "Add any specific questions or billing requirements..."
                }
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                className="mt-1 text-xs"
                rows={2}
                disabled={!isAdmin || !!pendingRequest}
              />
              <span className="text-[10px] text-gray-400 float-right mt-1">
                {notes.length}/500
              </span>
            </div>

            <DialogFooter className="gap-2 sm:gap-0 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>

              {isEnterprise ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    asChild
                  >
                    <a href={`mailto:${SALES_EMAIL}?subject=Enterprise Plan Enquiry`}>
                      Email Sales Directly
                    </a>
                  </Button>
                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    disabled={!isAdmin || !!pendingRequest || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Request Enterprise Call
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <Button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={!isAdmin || !!pendingRequest || isCurrentActivePlan || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : isCurrentActivePlan ? (
                    'Current Active Plan'
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Upgrade Request
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
